import Image from 'next/image';
import { useState } from 'react';

import { Box, Button, ButtonGroup, Text, TextButton } from '@zillow/constellation-10';
import { IconArrowRightOutline } from '@zillow/constellation-icons';

import {
  useRentalManagerUserClaimWebApiHandler_list,
  useRentalManagerUserClaimWebApiHandler_propertyLookupAll,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { LoadingParagraph } from '@zg-rentals/loading-components';

import numberUtils from '../../utils/numberUtils';
import { isEmailInContactMethods, isPhoneInContactMethods } from '../../utils/listingClaimUtils';
import stringHelper from '../../utils/stringHelper';

import WizardBodyTemplate from '../WizardBodyTemplate';

import ClaimListingCard from '../ClaimListingCard';

import PropertyLookupModal from '../modals/PropertyLookupModal';
import ManageClaimContactMethodsModal from '../modals/ManageClaimContactMethodsModal';

import HeartSearch from '../../assets/HeartSearch.svg';
import NoListings from './NoListings';

import type { UserClaimPropertyLookupAllListingDto } from '@zg-rentals/ts-rental-manager-api-client';
import type { WizardBodyTemplateProps } from '../WizardStepController';

const { formatPhoneNumberInput } = numberUtils;
const { pluralize } = stringHelper;

export const SKELETON_LISTING = {
  listingAlias: '',
  contactEmail: '',
  contactPhone: '',
  address: { city: '', state: '', street: '', unit: '', zip: '' },
  photoUrl: '',
  feedName: '',
  status: '',
  isActive: false,
};

const WizFindListingResults = ({ onGoToNextStep, progressStepper }: WizardBodyTemplateProps) => {
  const [showPropertyLookupModal, setShowPropertyLookupModal] = useState(false);
  const [showManageContactMethodsModal, setShowManageContactMethodsModal] = useState(false);

  const { data: listData, refetch: refetchList } = useRentalManagerUserClaimWebApiHandler_list();
  const {
    data: lookupData,
    refetch: refetchLookup,
    isFetching: isLookupFetching,
    isError: isLookupError,
  } = useRentalManagerUserClaimWebApiHandler_propertyLookupAll({
    includeVerified: true,
    includeUnverified: true,
  });

  const contactMethods = listData ? { emails: listData.emails, phones: listData.phones } : { emails: [], phones: [] };
  const contactMethodsCount = contactMethods.emails.length + contactMethods.phones.length;
  const listings: Array<UserClaimPropertyLookupAllListingDto> = isLookupFetching
    ? Array(10).fill(SKELETON_LISTING)
    : (lookupData?.listings ?? []);
  const listingCount = lookupData?.totalListingCount;
  const hasListings = isLookupFetching || Boolean(listingCount);
  const hasError = isLookupError;

  const handleContactMethodSuccessAdded = () => {
    setShowPropertyLookupModal(false);
    refetchList();
    refetchLookup();
  };

  const handleContactMethodsUpdated = (contactHasBeenUpdated = false) => {
    setShowManageContactMethodsModal(false);
    if (contactHasBeenUpdated) {
      refetchList();
      refetchLookup();
    }
  };

  const handleSearchForMoreListings = () => setShowPropertyLookupModal(true);
  const handleManageContact = () => setShowManageContactMethodsModal(true);
  const handleLookupListingClick = () => setShowPropertyLookupModal(true);

  const renderSubtitle = () => {
    if (isLookupFetching) {
      return (
        <LoadingParagraph isLoading loadingWidth="100%">
          &nbsp;
        </LoadingParagraph>
      );
    }
    if (hasListings) {
      const email = contactMethods.emails[0]?.value;
      const phone = contactMethods.phones[0]?.value;
      const contactToRender = email || formatPhoneNumberInput(phone ?? '');
      const listingSubtitle = `We found ${pluralize(listingCount ?? 0, 'listing', 'listings')}`;
      const contactMethodsSubtitle =
        contactMethodsCount > 1 ? (
          <>
            {' '}
            with{' '}
            <Text
              textStyle="body-bold"
              css={{ display: 'inline-block' }}
            >{`${contactMethodsCount} contact methods`}</Text>
          </>
        ) : (
          <>
            {' '}
            associated with{' '}
            <Text textStyle="body-bold" css={{ display: 'inline-block' }} className="fs-mask">
              {contactToRender}
            </Text>
          </>
        );
      return (
        <>
          {listingSubtitle}
          {contactMethodsSubtitle}
        </>
      );
    }
    return `Control how your listings appear on the Zillow Rental Network by transferring them
            to Zillow Rental Manager. You’ll also get access to features like online applications,
            online lease signing, and free virtual 3D tours.`;
  };

  return (
    <WizardBodyTemplate
      hasError={hasError}
      subtitle={renderSubtitle()}
      title={hasListings ? 'Your listings' : 'Find your listings to transfer'}
      mobileTitle={hasListings ? 'Listing results' : 'Find your listings to transfer'}
      icon={hasListings ? null : <Image src={HeartSearch} alt="HeartSearch" fill />}
      progressStepper={progressStepper}
      footer={
        <ButtonGroup aria-label="Find listing results" orientation="vertical">
          {hasListings && <TextButton onClick={handleSearchForMoreListings}>Search for more listings</TextButton>}
          <Button
            tone="brand"
            emphasis="filled"
            onClick={onGoToNextStep ?? undefined}
            disabled={!hasListings || isLookupFetching}
            icon={<IconArrowRightOutline />}
          >
            Next
          </Button>
        </ButtonGroup>
      }
    >
      {hasListings ? (
        <>
          {contactMethodsCount > 1 && (
            <Box css={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '400' }}>
              <TextButton onClick={handleManageContact}>Manage contact methods</TextButton>
            </Box>
          )}
          <Box css={{ borderBlock: '1px solid {colors.border.subtle}', md: { overflowY: 'auto', maxHeight: '940px' } }}>
            {listings.map((listing, index) => (
              <ClaimListingCard
                key={`ClaimListingCard-${isLookupFetching ? `skeleton-${index}` : listing?.listingAlias}`}
                listingData={listing}
                loading={isLookupFetching}
                highlightEmail={isEmailInContactMethods(contactMethods, listing?.contactEmail)}
                highlightPhone={isPhoneInContactMethods(contactMethods, listing?.contactPhone)}
              />
            ))}
          </Box>
        </>
      ) : (
        <NoListings onLookUpListingsClick={handleLookupListingClick} />
      )}
      {showPropertyLookupModal && (
        <PropertyLookupModal
          contactMethods={contactMethods}
          onContactMethodAddedSuccess={handleContactMethodSuccessAdded}
          onClose={() => setShowPropertyLookupModal(false)}
        />
      )}
      {showManageContactMethodsModal && <ManageClaimContactMethodsModal onClose={handleContactMethodsUpdated} />}
    </WizardBodyTemplate>
  );
};

export default WizFindListingResults;
